import { Modal } from "@/components/ui/Modal";
import { LayoutTemplate, ArrowRight, Sparkles } from "lucide-react";
import { useWorkflow } from "@/store/workflowStore";
import { useHive } from "@/store/useHive";
import { WORKFLOW_TEMPLATES } from "@/data/mockWorkflows";
import { nodeDef, KIND_META } from "@/data/mockWorkflowNodes";
import { APPS } from "@/data/apps";
import { gatedProductsIn } from "@/lib/entitlements";
import { RiskBadge } from "@/components/hive/shared";

export function WorkflowTemplatesModal() {
  const open = useWorkflow((s) => s.templatesOpen);
  const setOpen = useWorkflow((s) => s.setTemplatesOpen);
  const applyTemplate = useWorkflow((s) => s.applyTemplate);
  const entitlements = useHive((s) => s.entitlements);

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      title="Workflow Templates"
      subtitle="Start from a proven Hook → Process → Publish pattern"
      icon={<LayoutTemplate size={16} />}
      accent="#f43f5e"
      width={760}
    >
      <div className="grid grid-cols-2 gap-3">
        {WORKFLOW_TEMPLATES.map((t) => {
          const steps = [...t.hookKeys, ...t.processKeys, ...t.publishKeys];
          // Templates with Early-Access product steps stay usable as drafts —
          // the badge tells you activation needs the product.
          const gated = gatedProductsIn(
            steps.map((k) => ({ integration: nodeDef(k).integration })),
            entitlements,
          );
          return (
            <div key={t.id} className="flex flex-col rounded-xl border border-app bg-surface/40 p-3 transition hover:border-app-strong">
              <div className="flex items-start justify-between gap-2">
                <div className="text-sm font-semibold text-app">{t.name}</div>
                <RiskBadge risk={t.risk} />
              </div>
              {gated.length > 0 && (
                <span
                  className="mt-1 inline-flex w-fit items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide"
                  style={{ background: `${APPS[gated[0]].accent}22`, color: APPS[gated[0]].accent }}
                >
                  <Sparkles size={8} /> Requires {gated.map((p) => APPS[p].name).join(" + ")} · Early Access
                </span>
              )}
              <div className="mt-1 text-[11px] leading-relaxed text-app-2">{t.description}</div>

              <div className="mt-2.5 flex flex-wrap items-center gap-1">
                {steps.slice(0, 7).map((k, i) => {
                  const d = nodeDef(k);
                  const c = KIND_META[d.kind].color;
                  return (
                    <span key={i} className="flex items-center gap-1">
                      <span className="flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[9.5px] font-medium" style={{ color: c, borderColor: `${c}44`, background: `${c}12` }}>
                        <d.icon size={10} /> {d.label.replace("Run ", "").replace("Create ", "")}
                      </span>
                      {i < Math.min(steps.length, 7) - 1 && <ArrowRight size={9} className="text-app-2" />}
                    </span>
                  );
                })}
                {steps.length > 7 && <span className="text-[10px] text-app-2">+{steps.length - 7}</span>}
              </div>

              <div className="mt-3 flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider text-app-2">{t.category}</span>
                <button onClick={() => applyTemplate(t)} className="flex items-center gap-1 rounded-lg bg-rose-500 px-3 py-1.5 text-[11px] font-semibold text-white transition hover:brightness-110">
                  Use template <ArrowRight size={12} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
